/**
 * The recognition flow without the service (`?recognition=demo`): the upload, the queue and the stages are played on
 * timers, and the answer is the ready-made «Дом в два этажа» — one of its floors per plan, whatever the pictures show.
 * It exists to show the flow before the company's service is connected; the dialog says so while it is on.
 *
 * `recognitionScore` sets the score (the gates: below 90 the plan opens on the 2D plan, below 97 it asks to be
 * checked) and `recognitionFail` a failure code, both from the page's address.
 */
import { RecognitionError, type FailureCode, type JobStatus, type RecognitionTransport } from './protocol';

const DEMO_HOUSE = 'https://icandesignapp.com/projects/6a8461383c7b8de5bc0ce6be/project';
const UPLOAD_MS = 1200;
/** When each stage starts, ms after the upload. */
const TIMELINE: [number, JobStatus['stage']][] = [[0, 'queued'], [1800, 'detecting'], [5200, 'assembling'], [7600, 'saving']];
const DONE_MS = 8600;
const HOUSE_FLOORS = 2;

interface DemoJob { started: number; floors: number; cancelled: boolean }

const aborted = () => new DOMException('Aborted', 'AbortError');
const sleep = (ms: number, signal: AbortSignal) => new Promise<void>((resolve, reject) => {
  if (signal.aborted) { reject(aborted()); return; }
  const timer = setTimeout(() => { signal.removeEventListener('abort', stop); resolve(); }, ms);
  const stop = () => { clearTimeout(timer); reject(aborted()); };
  signal.addEventListener('abort', stop, { once: true });
});

/** The house's first `floors` levels, the rest cut off. */
export function keepLevels(xml: string, floors: number): string {
  const document = new DOMParser().parseFromString(xml, 'application/xml');
  const levels = document.querySelector('Building > Levels');
  if (!levels) throw new RecognitionError('server', 'The example has no levels');
  [...levels.children].filter(level => level.tagName === 'Level').slice(Math.max(1, floors)).forEach(level => level.remove());
  return new XMLSerializer().serializeToString(document);
}

export function createDemoTransport(options: { score?: number; fail?: FailureCode | null; now?: () => number } = {}): RecognitionTransport {
  const now = options.now ?? (() => performance.now());
  const jobs = new Map<string, DemoJob>();
  const status = (id: string): JobStatus => {
    const job = jobs.get(id);
    if (!job) throw new RecognitionError('server', 'Unknown job');
    const elapsed = now() - job.started;
    const floors = Array.from({ length: job.floors }, (_, index) => index);
    if (job.cancelled) return { id, state: 'cancelled', stage: 'queued', floors: [] };
    if (elapsed >= DONE_MS) {
      if (options.fail) return { id, state: 'failed', stage: 'assembling', floors: floors.map(index => ({ index, state: 'not_recognized' })), error: { code: options.fail } };
      // The example house has two floors: a third plan is «не распознан», as a floor the service could not read.
      return { id, state: 'done', stage: 'saving',
        floors: floors.map(index => index < HOUSE_FLOORS ? { index, state: 'ready', score: options.score ?? 93 } : { index, state: 'not_recognized' }),
        result: { projectUrl: 'demo:' + Math.min(HOUSE_FLOORS, job.floors), score: options.score ?? 93 } };
    }
    const stage = TIMELINE.filter(([start]) => elapsed >= start).pop()![1];
    const running = Math.min(job.floors - 1, Math.floor(Math.max(0, elapsed - 1800) / ((DONE_MS - 1800) / job.floors)));
    return {
      id, state: stage === 'queued' ? 'queued' : 'running', stage,
      ...(stage === 'queued' ? { position: elapsed < 900 ? 1 : 0 } : { progress: Math.min(0.97, (elapsed - 1800) / (DONE_MS - 1800)) }),
      etaSeconds: DONE_MS / 1000,
      floors: floors.map(index => ({ index, state: stage === 'queued' || index > running ? 'waiting' : index < running ? 'ready' : 'running' })),
    };
  };
  return {
    demo: true,
    async start({ floors }, onUpload, signal) {
      for (let step = 1; step <= 6; step++) {
        await sleep(UPLOAD_MS / 6, signal);
        onUpload(step / 6);
      }
      const id = 'demo-' + Math.random().toString(36).slice(2);
      jobs.set(id, { started: now(), floors: floors.length, cancelled: false });
      return status(id);
    },
    async status(id, signal) {
      if (signal.aborted) throw aborted();
      return status(id);
    },
    async project(job, signal) {
      let response: Response;
      try { response = await fetch(DEMO_HOUSE, { signal }); } catch (error) {
        if (signal.aborted) throw aborted();
        throw new RecognitionError('network', String(error));
      }
      if (!response.ok) throw new RecognitionError('server', `HTTP ${response.status}`);
      return keepLevels(await response.text(), Number(job.result?.projectUrl.slice('demo:'.length)) || 1);
    },
    cancel(id) {
      const job = jobs.get(id);
      if (job) job.cancelled = true;
    },
  };
}
